import NonProductionAssessments from './NonProductionAssessments';
import React, { useEffect, useState } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import GradePlacements from './GradePlacements';
import GradeAssessments from './GradeAssessments';
import GradePayApprovals from './GradePayApprovals';

const money = value => '\u20a6' + Number(value || 0).toLocaleString('en-NG', { maximumFractionDigits: 2 });
const names = { handle_preparation: 'Handle preparation', handle_attachment: 'Handle attachment',
  gusset_attachment: 'Gusset attachment', bow_making: 'Bow making' };
const box = { border: '1px solid var(--border-light, #ddd)', borderRadius: 8, padding: 16, marginTop: 16 };

function Requirements({ item }) {
  const rules = item.rules;
  if (rules.kind === 'role_competence') return <section style={box}>
    <h3>{item.level_label}{rules.expansion_only ? ' — Future expansion role' : ''}</h3>
    <p className="text-muted">{item.status} · Rule version {item.version} · {rules.reporting_line}</p>
    <ul style={{ paddingLeft: 22, lineHeight: 1.8 }}>{rules.skills.map(skill => <li key={skill}>{skill}</li>)}</ul>
    <p>Promotion review: three consecutive months of documented performance against targets agreed before assessment, independent work at the higher grade, and verified examples of judgement and problem-solving.</p>
    <p>{rules.expansion_only ? 'Appointment also requires an approved business need and an available role. ' : ''}Management approval is required. Grade, access permissions and salary decisions are separate.</p>
  </section>;
  return <section style={box}>
    <div className="flex-between"><h3 style={{ margin: 0 }}>{item.level_code} — {item.level_label}</h3>
      <span className="text-muted text-sm">{item.status} · Rule version {item.version}</span></div>
    <ul style={{ paddingLeft: 22, lineHeight: 1.8 }}>{rules.skills.map(skill => <li key={skill}>{skill}</li>)}</ul>
    {rules.kind === 'training' ? <p className="text-muted">Supervised entry-level training.</p> : <>
      <p><strong>{rules.assessment_days} qualifying working days</strong> · At least {rules.first_pass_qc_min_percent}% first-pass QC for each activity.</p>
      {rules.activities?.length > 0 && <div style={{ overflowX: 'auto' }}><table className="table" style={{ width: '100%' }}>
        <thead><tr><th>Activity</th><th>Daily target</th><th>Assessment days</th><th>Target days required</th><th>Final target streak</th></tr></thead>
        <tbody>{rules.activities.map(activity => <tr key={activity.code}>
          <td>{names[activity.code] || activity.code}</td>
          <td>{activity.daily_target ? `${activity.daily_target.toLocaleString()} ${activity.unit === 'individual_handle_piece' ? 'pieces' : 'bags'}` : 'Independent demonstration'}</td>
          <td>{activity.required_days}</td><td>{activity.minimum_target_days || 'Skill verified'}</td>
          <td>{activity.final_consecutive_target_days ? `${activity.final_consecutive_target_days} working days` : 'Skill verified'}</td>
        </tr>)}</tbody>
      </table></div>}
      {rules.handle_pieces_per_bag && <p className="text-muted text-sm">Two handle pieces per bag: 1,000 prepared pieces = 500 bag sets; 600 attached pieces = 300 bags.</p>}
      {rules.benchmark && <p><strong>Benchmark:</strong> jute bag, finished size {rules.benchmark.finished_dimensions_inches.join(' × ')} inches.
        {' '}The target counts gusset attachments.</p>}
      {rules.successful_independent_assignments_per_target_skill && <p>{rules.successful_independent_assignments_per_target_skill} successful independent assignments for every listed skill.</p>}
      {rules.template_dimension_source === 'supplied_dimensions' && <p>Templates use dimensions supplied for the assignment.</p>}
      <p className="text-muted text-sm">All accepted output must pass final QC. Rework counts once and keeps its original first-pass QC result.
        {' '}Lower-grade skills must remain mastered. Verified material or machine interruptions require replacement working days.</p>
    </>}
  </section>;
}

export default function GradeConfiguration({ onClose }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [familyCode, setFamilyCode] = useState('production');
  const [careerTab, setCareerTab] = useState('placements');
  const [trackCode, setTrackCode] = useState('tailor');
  const [scheduleId, setScheduleId] = useState('');
  const [review, setReview] = useState(null);
  const [withdraw, setWithdraw] = useState(null);
  const canReview = user?.role === 'super_admin';
  const isPlanning = familyCode !== 'production';

  useEffect(() => {
    let live = true;
    get('/grading/catalogue').then(result => {
      if (live && result?.data) { setData(result.data); setError(''); }
    }).catch(err => { if (live) setError(err.message); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);

  const reload = async preferred => {
    const result = await get('/grading/catalogue');
    if (!result?.data) throw new Error('Your session has expired. Please sign in again.');
    setData(result.data);
    if (preferred) setScheduleId(preferred);
  };
  const family = data?.families.find(item => item.code === familyCode);
  const levels = data?.levels.filter(item => item.family_code === familyCode) || [];
  const tracks = data?.tracks.filter(item => item.family_code === familyCode) || [];
  const schedules = data?.schedules.filter(item => item.family_code === familyCode) || [];
  const selected = schedules.find(item => item.id === scheduleId) || schedules.find(item => item.status !== 'withdrawn') || schedules[0];
  const seen = new Set();
  const ruleSets = (data?.rules || []).filter(item => {
    if (item.family_code !== familyCode || item.track_code !== trackCode || seen.has(item.level_code)) return false;
    seen.add(item.level_code); return true;
  });

  const startReview = () => {
    setError(''); setNotice(''); setWithdraw(null);
    setReview({ reason: '', rates: levels.map(level => ({ level_code: level.code,
      base_amount: selected?.rates.find(rate => rate.level_code === level.code)?.base_amount || '' })) });
  };
  const saveReview = async event => {
    event.preventDefault(); if (busy) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await post('/grading/salary-reviews', { family_code: familyCode, ...review });
      if (!result?.data) throw new Error('Your session has expired. Please sign in again.');
      setReview(null);
      setNotice(result.message);
      try { await reload(result.data.id); }
      catch (_) { setError('The draft was saved, but the catalogue could not refresh. Close and reopen this screen to view it.'); }
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const saveWithdrawal = async event => {
    event.preventDefault(); if (busy) return;
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await put('/grading/salary-reviews/' + withdraw.id + '/withdraw', { reason: withdraw.reason });
      if (!result?.data) throw new Error('Your session has expired. Please sign in again.');
      setWithdraw(null); setNotice(result.message);
      try { await reload(); }
      catch (_) { setError('The draft was withdrawn, but the catalogue could not refresh. Close and reopen this screen.'); }
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  return <div className="modal-overlay"><div className="modal" role="dialog" aria-modal="true" aria-labelledby="grade-config-title"
    style={{ maxWidth: 1000, width: '95vw', maxHeight: '92vh', overflowY: 'auto' }}>
    <div className="modal-hdr"><h2 id="grade-config-title" className="modal-title">Staff Grades &amp; Salary Reviews</h2>
      <button className="btn btn-ghost btn-sm" disabled={busy} onClick={onClose} aria-label="Close grading">Close</button></div>
    <div className="modal-body">
      {error && <div role="alert" style={{ ...box, color: '#8F2E24', marginTop: 0 }}>{error}</div>}
      {notice && <div role="status" style={{ ...box, color: '#556B2F', marginTop: 0 }}>{notice}</div>}
      {loading ? <p role="status">Loading grading configuration…</p> : !data ? <p>Close and reopen this screen to try again.</p> : <>
        <p className="text-muted">Choose a department, then open the section you need.</p>
        <nav aria-label="Staff career sections" style={{display:'flex',gap:8,flexWrap:'wrap',margin:'12px 0'}}>
          {[['placements','Staff Grades'],['assessments','Assessments'],['salary','Salary Reviews'],['requirements','Grade Requirements']].map(([key,label])=>
            <button type="button" key={key} className={'btn '+(careerTab===key?'btn-primary':'btn-outline')}
              aria-pressed={careerTab===key} disabled={busy || !!review || !!withdraw} onClick={()=>{setCareerTab(key);setError('');setNotice('');}}>{label}</button>)}
        </nav>
        <div className="form-group" style={{ marginTop: 16 }}><label className="form-label" htmlFor="grade-family">Department / career family</label>
          <select id="grade-family" className="form-control" value={familyCode} disabled={busy || !!review || !!withdraw}
            onChange={event => { const code = event.target.value; setFamilyCode(code); setScheduleId('');
              setTrackCode(data.tracks.find(item => item.family_code === code)?.code || ''); setNotice(''); setError(''); }}>
            {data.families.map(item => <option key={item.code} value={item.code}>{item.label}</option>)}
          </select></div>
        {family ? <>
          <div hidden={careerTab!=='salary'}>
          <section style={box}><div className="flex-between" style={{ flexWrap: 'wrap', gap: 12 }}>
            <h3 style={{ margin: 0 }}>{isPlanning ? 'Salary planning figures' : 'Monthly base salaries'}</h3>
            {canReview && levels.length > 0 && !review && !withdraw && family.rollout_status !== 'retired' &&
              <button className="btn btn-outline" disabled={busy} onClick={startReview}>Create Salary Review Draft</button>}
          </div>
          {schedules.length > 0 ? <>
            <label className="form-label" htmlFor="grade-schedule" style={{ marginTop: 16 }}>Salary review history</label>
            <select id="grade-schedule" className="form-control" value={selected?.id || ''} disabled={busy || !!review || !!withdraw}
              onChange={event => setScheduleId(event.target.value)}>
              {schedules.map(item => <option key={item.id} value={item.id}>Version {item.version} — {item.status}
                {item.effective_from ? ` — effective ${String(item.effective_from).slice(0, 10)}` : ' — no effective date'}</option>)}
            </select>
            <div style={{ overflowX: 'auto', marginTop: 12 }}><table className="table" style={{ width: '100%' }}>
              <thead><tr><th>Grade</th><th>Level</th><th>Monthly base</th></tr></thead>
              <tbody>{levels.map(level => { const rate = selected?.rates.find(x => x.level_code === level.code); return <tr key={level.code}><td>{level.code}</td><td>{level.label}</td><td>{rate ? money(rate.base_amount) : 'Not set'}</td></tr>; })}</tbody>
            </table></div>
            <p><strong>Review reason:</strong> <span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{selected?.review_reason}</span></p>
            {selected?.status === 'draft' && <p className="text-muted text-sm">These rates are proposed. No effective date or salary activation has been approved.</p>}
            {canReview && selected?.status === 'draft' && !review && !withdraw &&
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => { setError(''); setNotice(''); setWithdraw({ id: selected.id, reason: '' }); }}>Withdraw This Draft</button>}
          </> : <p>Salary figures are not set for this family. Current staff pay stays unchanged.</p>}

          {review && <form onSubmit={saveReview} style={{ ...box, background: '#F2EFE7' }}>
            <h4 style={{ marginTop: 0 }}>New salary review draft</h4>
            {isPlanning && <p>Leave undecided salaries blank. These planning figures do not change current pay. Known higher-grade figures must exceed known lower-grade figures.</p>}
            {review.rates.map((rate, index) => <div className="form-group" key={rate.level_code}>
              <label className="form-label" htmlFor={'grade-rate-' + rate.level_code}>{rate.level_code} — {levels.find(level => level.code === rate.level_code)?.label} (₦ per month)</label>
              <input id={'grade-rate-' + rate.level_code} className="form-control" type="number" required={!isPlanning} min="0.01" max="999999999999.99" step="0.01"
                value={rate.base_amount} disabled={busy} onChange={event => { const value = event.target.value;
                  setReview(current => ({ ...current, rates: current.rates.map((r, i) => i === index ? { ...r, base_amount: value } : r) })); }} />
            </div>)}
            <label className="form-label" htmlFor="grade-review-reason">Reason for salary review</label>
            <textarea id="grade-review-reason" className="form-control" required minLength={10} maxLength={2000} rows={3} disabled={busy}
              value={review.reason} onChange={event => setReview({ ...review, reason: event.target.value })} />
            <p className="text-muted text-sm">Saving creates a new version and preserves previous rates. Staff salaries and payroll remain unchanged.</p>
            <div className="flex-center gap-sm"><button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Save Draft'}</button>
              <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => { setReview(null); setError(''); }}>Cancel</button></div>
          </form>}
          {withdraw && <form onSubmit={saveWithdrawal} style={box}>
            <label className="form-label" htmlFor="grade-withdraw-reason">Reason for withdrawing this draft</label>
            <textarea id="grade-withdraw-reason" className="form-control" required minLength={10} maxLength={2000} rows={3} disabled={busy}
              value={withdraw.reason} onChange={event => setWithdraw({ ...withdraw, reason: event.target.value })} />
            <p>The draft and its rates will remain in the review history.</p>
            <div className="flex-center gap-sm"><button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Withdrawing…' : 'Withdraw Draft'}</button>
              <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => { setWithdraw(null); setError(''); }}>Cancel</button></div>
          </form>}
          </section>
          {familyCode === 'production' && <GradePayApprovals catalogue={data} onBusy={setBusy} onChanged={reload} />}
          </div>
          <div hidden={careerTab!=='placements'}><GradePlacements key={familyCode} familyCode={familyCode} catalogue={data} onBusy={setBusy} /></div>
          <div hidden={careerTab!=='assessments'}>
            {familyCode !== 'production' ? <NonProductionAssessments key={'nonprod-'+familyCode} familyCode={familyCode} onBusy={setBusy} /> : <GradeAssessments onBusy={setBusy} />}
          </div>
          <div hidden={careerTab!=='requirements'}>
          <section style={box}><h3 style={{ marginTop: 0 }}>Grade requirements</h3>
            <label className="form-label" htmlFor="grade-track">Career track</label>
            <select id="grade-track" className="form-control" value={trackCode} onChange={event => setTrackCode(event.target.value)}>
              {tracks.map(track => <option key={track.code} value={track.code}>{track.label}</option>)}
            </select>
            {ruleSets.length ? ruleSets.map(item => <Requirements key={item.id} item={item} />) : <p>No requirements have been configured for this track.</p>}
          </section>
          </div>
        </> : <p>No grade family is configured. Non-production grades will be defined separately.</p>}
      </>}
    </div>
  </div></div>;
}
